import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Loader2,
  RefreshCw,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { supabase } from '@/services/db/supabase';
import { useAuthStore } from '@/store/authStore';
import {
  computeRiskScore,
  setRiskLevelOverride,
  setRiskSbarNotes,
  type ActivityRiskLevel,
  type RiskScoreBreakdown,
} from '@/services/db/riskScore';

const LEVELS: ActivityRiskLevel[] = ['low', 'medium', 'high', 'critical'];

const ASSESSMENT_MAX = 1000;
const RECOMMENDATION_MAX = 500;
const SCORE_MAX = 100;

const RISK_BADGE: Record<ActivityRiskLevel, { className: string; label: string }> = {
  low: { className: 'bg-mozaic-green/15 text-mozaic-green', label: 'Low' },
  medium: { className: 'bg-mozaic-orange/15 text-mozaic-orange', label: 'Medium' },
  high: { className: 'bg-mozaic-red/15 text-mozaic-red', label: 'High' },
  critical: { className: 'bg-mozaic-red/20 text-mozaic-red font-bold', label: 'Critical' },
};

interface RiskRow {
  id: string;
  tenant_id: string;
  status: string;
  overall_risk_level: ActivityRiskLevel | null;
  risk_score: number | null;
  risk_score_breakdown: RiskScoreBreakdown | null;
  risk_score_computed_at: string | null;
  risk_level_override: ActivityRiskLevel | null;
  risk_level_override_reason: string | null;
  risk_level_override_by: string | null;
  risk_assessment_note: string | null;
  risk_recommendation_note: string | null;
}

interface Props {
  dsrId: string;
  isReadOnly: boolean;
}

export default function RiskAssessmentForm({ dsrId, isReadOnly }: Props) {
  const userId = useAuthStore((s) => s.user?.id);
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['dsr_risk_row', dsrId],
    queryFn: async (): Promise<RiskRow> => {
      const { data, error } = await supabase
        .from('dsr_reports')
        .select(
          'id, tenant_id, status, overall_risk_level, risk_score, ' +
          'risk_score_breakdown, risk_score_computed_at, ' +
          'risk_level_override, risk_level_override_reason, risk_level_override_by, ' +
          'risk_assessment_note, risk_recommendation_note'
        )
        .eq('id', dsrId)
        .single();
      if (error) throw error;
      return data as unknown as RiskRow;
    },
  });

  const [overrideEnabled, setOverrideEnabled] = useState(false);
  const [overrideLevel, setOverrideLevel] = useState<ActivityRiskLevel>('medium');
  const [reason, setReason] = useState('');
  const [assessmentNote, setAssessmentNote] = useState('');
  const [recommendationNote, setRecommendationNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [howOpen, setHowOpen] = useState(false);

  useEffect(() => {
    if (!data) return;
    if (data.risk_level_override) {
      setOverrideEnabled(true);
      setOverrideLevel(data.risk_level_override);
      setReason(data.risk_level_override_reason ?? '');
    } else {
      setOverrideEnabled(false);
      setReason('');
    }
    setAssessmentNote(data.risk_assessment_note ?? '');
    setRecommendationNote(data.risk_recommendation_note ?? '');
  }, [
    data?.risk_level_override,
    data?.risk_level_override_reason,
    data?.risk_assessment_note,
    data?.risk_recommendation_note,
  ]);

  const computedLevel: ActivityRiskLevel | null = useMemo(() => {
    if (!data?.risk_score && data?.risk_score !== 0) return null;
    const s = data.risk_score;
    if (s >= 60) return 'critical';
    if (s >= 40) return 'high';
    if (s >= 20) return 'medium';
    return 'low';
  }, [data?.risk_score]);

  const hasScore = data?.risk_score !== null && data?.risk_score !== undefined;

  async function handleRecompute() {
    if (!data) return;
    setRecomputing(true);
    try {
      await computeRiskScore({ dsrId: data.id, tenantId: data.tenant_id });
      await refetch();
      await queryClient.invalidateQueries({ queryKey: ['dsr_detail', data.id] });
    } finally {
      setRecomputing(false);
    }
  }

  async function handleSave() {
    if (!data || !userId) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Always persist the SBAR narrative fields — they are independent
      // of override state. Empty strings store as NULL.
      await setRiskSbarNotes({
        dsrId: data.id,
        assessmentNote: assessmentNote.trim() ? assessmentNote : null,
        recommendationNote: recommendationNote.trim() ? recommendationNote : null,
      });

      // Override triad is conditional.
      if (overrideEnabled) {
        if (!reason.trim()) {
          setSaveError('A justification is required when overriding the risk level.');
          return;
        }
        await setRiskLevelOverride({
          dsrId: data.id,
          level: overrideLevel,
          reason: reason.trim(),
          userId,
        });
      } else if (data.risk_level_override) {
        // Was overridden, now cleared — null the triad and re-run the
        // engine so overall_risk_level reverts to computed.
        await setRiskLevelOverride({
          dsrId: data.id,
          level: null,
          reason: null,
          userId,
        });
        await computeRiskScore({ dsrId: data.id, tenantId: data.tenant_id });
      }

      await refetch();
      await queryClient.invalidateQueries({ queryKey: ['dsr_detail', data.id] });
    } catch (err: any) {
      setSaveError(err?.message ?? 'Failed to save risk assessment.');
    } finally {
      setSaving(false);
    }
  }

  if (isLoading || !data) {
    return (
      <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading risk assessment…
      </div>
    );
  }

  const breakdown = data.risk_score_breakdown;
  const isDraft = data.status === 'draft';
  const assessmentOver = assessmentNote.length > ASSESSMENT_MAX;
  const recommendationOver = recommendationNote.length > RECOMMENDATION_MAX;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">Risk Assessment — SBAR Briefing</h2>
        <p className="text-sm text-muted-foreground">
          Read the computed composite risk, then add your Assessment and
          Recommendation for the next shift and reviewing supervisor.
          Overriding the computed level is itself an assessment act and
          lives in the Assessment zone.
        </p>
      </div>

      {/* ── S — SITUATION ─────────────────────────────────────── */}
      <Card>
        <CardContent className="space-y-3 py-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              S &mdash; Situation
            </h3>
            <Button
              size="sm"
              variant="outline"
              disabled={recomputing || isReadOnly}
              onClick={handleRecompute}
            >
              <RefreshCw className={`mr-1.5 h-4 w-4 ${recomputing ? 'animate-spin' : ''}`} />
              {hasScore ? 'Recalculate' : 'Compute now'}
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {computedLevel ? (
              <>
                <Badge className={RISK_BADGE[computedLevel].className}>
                  {RISK_BADGE[computedLevel].label}
                </Badge>
                <span className="text-sm font-medium">
                  {data.risk_score}/{SCORE_MAX} pts
                </span>
                {data.risk_level_override && (
                  <Badge variant="outline" className="border-mozaic-blue text-mozaic-blue">
                    Override active: {RISK_BADGE[data.risk_level_override].label}
                  </Badge>
                )}
                {data.risk_score_computed_at && (
                  <span className="text-xs text-muted-foreground">
                    Computed {format(new Date(data.risk_score_computed_at), "MMM d, yyyy 'at' HH:mm")}
                  </span>
                )}
              </>
            ) : (
              <span className="text-sm text-muted-foreground">
                {isDraft
                  ? 'Not yet computed — score is generated on submit.'
                  : 'Not yet computed.'}
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={() => setHowOpen((v) => !v)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            {howOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            How is this calculated?
          </button>

          {howOpen && (
            <div className="space-y-2 rounded-md bg-muted/50 px-3 py-2 text-sm">
              <p className="font-medium">How the risk score is calculated</p>
              <p>We combine four bands of evidence from this DSR:</p>
              <ul className="ml-4 list-disc space-y-1">
                <li>
                  <strong>Activities</strong> (up to 40 pts) — critical / high-risk
                  activities, high-hazard tags (hot work, confined space,
                  critical lift, high pressure), and JSA quality.
                </li>
                <li>
                  <strong>Safety events</strong> (up to 35 pts) — the most severe event
                  type reported drives this band, with bonuses if an SWA
                  was filed.
                </li>
                <li>
                  <strong>Workforce</strong> (up to 15 pts) — elevated SSE% on the crew,
                  weighted to new-to-industry personnel.
                </li>
                <li>
                  <strong>Permits</strong> (up to 10 pts) — active hot work, confined
                  space, critical lift, and any suspensions.
                </li>
              </ul>
              {computedLevel && data.risk_score !== null ? (
                <p>
                  Your score <strong>{data.risk_score}/{SCORE_MAX}</strong> falls into
                  the <strong>{RISK_BADGE[computedLevel].label}</strong> band
                  (0–19 Low • 20–39 Medium • 40–59 High • 60+ Critical).
                  The specific signals that contributed are listed in
                  Background below.
                </p>
              ) : (
                <p>
                  Levels: 0–19 Low • 20–39 Medium • 40–59 High • 60+ Critical.
                  The specific signals that contributed will be listed in
                  Background once the score is computed.
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                An SSR can override the computed level in the Assessment
                section, with a justification.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── B — BACKGROUND ────────────────────────────────────── */}
      <Card>
        <CardContent className="space-y-3 py-4">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            B &mdash; Background
          </h3>

          {breakdown ? (
            <>
              <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 rounded-md bg-muted/50 px-3 py-2 text-sm">
                <span className="text-muted-foreground">Activity signals</span>
                <span className="text-right font-mono">+{breakdown.activity_points}</span>
                <span className="text-muted-foreground">Safety event signals</span>
                <span className="text-right font-mono">+{breakdown.safety_event_points}</span>
                <span className="text-muted-foreground">Workforce signals</span>
                <span className="text-right font-mono">+{breakdown.workforce_points}</span>
                <span className="text-muted-foreground">Permit signals</span>
                <span className="text-right font-mono">+{breakdown.permit_points}</span>
                <span className="border-t pt-1 font-medium">Total score</span>
                <span className="border-t pt-1 text-right font-mono font-semibold">
                  {breakdown.total}/{SCORE_MAX}
                </span>
              </div>

              {breakdown.signals.length > 0 ? (
                <div>
                  <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">
                    Signals fired
                  </p>
                  <ul className="ml-4 list-disc space-y-0.5 text-sm">
                    {breakdown.signals.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No risk signals fired on this report.
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Per-band breakdown appears once the score is computed.
            </p>
          )}
        </CardContent>
      </Card>

      {/* ── A — ASSESSMENT ────────────────────────────────────── */}
      <Card>
        <CardContent className="space-y-4 py-4">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            A &mdash; Assessment
          </h3>

          <div className="space-y-1">
            <Label htmlFor="risk_assessment_note" className="text-xs">
              Your assessment (optional)
            </Label>
            <Textarea
              id="risk_assessment_note"
              value={assessmentNote}
              onChange={(e) => setAssessmentNote(e.target.value)}
              placeholder="What does this risk profile mean for this crew on this shift?"
              rows={4}
              disabled={isReadOnly}
            />
            <p
              className={`text-xs ${
                assessmentOver ? 'text-mozaic-red' : 'text-muted-foreground'
              }`}
            >
              {assessmentNote.length}/{ASSESSMENT_MAX} characters
              {assessmentOver && ' — over recommended length'}
            </p>
          </div>

          <div className="space-y-3 rounded-md border border-muted-foreground/20 px-3 py-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={overrideEnabled}
                onChange={(e) => setOverrideEnabled(e.target.checked)}
                disabled={isReadOnly}
                className="h-4 w-4"
              />
              <span className="text-sm font-medium">
                Override computed risk level
              </span>
            </label>

            {overrideEnabled && (
              <div className="space-y-3 border-l-2 border-mozaic-blue/40 pl-3">
                <div className="space-y-1">
                  <Label className="text-xs">Override to</Label>
                  <div className="flex flex-wrap gap-2">
                    {LEVELS.map((level) => {
                      const badge = RISK_BADGE[level];
                      const selected = overrideLevel === level;
                      return (
                        <button
                          key={level}
                          type="button"
                          disabled={isReadOnly}
                          onClick={() => setOverrideLevel(level)}
                          className={`rounded-md px-3 py-1 text-sm transition-colors ${
                            selected
                              ? `${badge.className} ring-2 ring-offset-1 ring-current`
                              : 'border border-muted-foreground/30 text-muted-foreground hover:bg-muted'
                          }`}
                        >
                          {badge.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="override_reason" className="text-xs">
                    Justification <span className="text-mozaic-red">*</span>
                  </Label>
                  <Textarea
                    id="override_reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Why is the computed level being overridden?"
                    rows={3}
                    disabled={isReadOnly}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── R — RECOMMENDATION ────────────────────────────────── */}
      <Card>
        <CardContent className="space-y-3 py-4">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            R &mdash; Recommendation
          </h3>

          <div className="space-y-1">
            <Label htmlFor="risk_recommendation_note" className="text-xs">
              Recommendation for next shift / supervisor (optional)
            </Label>
            <Textarea
              id="risk_recommendation_note"
              value={recommendationNote}
              onChange={(e) => setRecommendationNote(e.target.value)}
              placeholder="What should the next shift or supervisor do about this risk profile?"
              rows={3}
              disabled={isReadOnly}
            />
            <p
              className={`text-xs ${
                recommendationOver ? 'text-mozaic-red' : 'text-muted-foreground'
              }`}
            >
              {recommendationNote.length}/{RECOMMENDATION_MAX} characters
              {recommendationOver && ' — over recommended length'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ── Save ──────────────────────────────────────────────── */}
      {saveError && (
        <div className="flex items-center gap-2 rounded-md bg-mozaic-red/10 px-3 py-2 text-sm text-mozaic-red">
          <AlertTriangle className="h-4 w-4" />
          {saveError}
        </div>
      )}

      <Button
        size="sm"
        disabled={isReadOnly || saving}
        onClick={handleSave}
        className="bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
      >
        {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
        Save risk assessment
      </Button>
    </div>
  );
}
